<script setup lang="ts">
import BaseCluster from '~/components/foundations/BaseCluster.vue'
import BaseStack from '~/components/foundations/BaseStack.vue'
import NumericKeyboard from '~/components/foundations/NumericKeyboard/NumericKeyboard.vue'
import type { Key } from '~/components/foundations/NumericKeyboard/NumericKeyboard.vue'
import TextBody from '~/components/foundations/TextBody.vue'
import BaseDialog from '~/components/molecules/BaseDialog.vue'

const isActive = defineModel<boolean>('isActive', { required: true })

const props = defineProps<{
  initialValue: number | null
  displayName: string
}>()

const initialValueString = computed(() => {
  return props.initialValue === null ? '' : props.initialValue.toString()
})

const emits = defineEmits<{
  (e: 'submitValue', value: number | null): void
}>()

const editingInputValue = ref<string>(initialValueString.value)

const digitCount = computed(() => {
  return editingInputValue.value.replace(/[^0-9]/g, '').length
})

const keyPress = (key: Key) => {
  if (key === 'backspace') {
    editingInputValue.value = editingInputValue.value.slice(0, -1)
    return
  }

  // 桁数が多い場合考慮する(はみ出しや桁数が多い場合の指数標記を防ぐ)ことが増えるため制限している
  if (digitCount.value >= 10) {
    return
  }

  editingInputValue.value += key
}

const fixValue = (value: string) => {
  if (value === initialValueString.value) {
    // 無駄なemitを避ける
    isActive.value = false
    return
  }

  if (value === '') {
    emits('submitValue', null)
    return
  }

  const val = Number(value)

  if (isNaN(val)) {
    // 数値変換できない場合は何もしない
    isActive.value = false
    return
  }

  emits('submitValue', val)
}
</script>

<template>
  <BaseDialog
    v-model:is-active="isActive"
    :title="$t('数値入力')"
    :buttons="{
      ok: { text: $t('完了') },
    }"
    @ok="() => fixValue(editingInputValue)"
  >
    <template #content>
      <BaseStack gap="l">
        <BaseCluster :justify="'space-between'" gap="m">
          <TextBody size="large" class="check-item-title">
            {{ displayName }}
          </TextBody>
        </BaseCluster>
        <BaseStack gap="m">
          <div class="input-field">
            {{ editingInputValue }}
          </div>
          <NumericKeyboard @key-press="keyPress" />
        </BaseStack>
      </BaseStack>
    </template>
  </BaseDialog>
</template>

<style lang="scss" scoped>
.input-field {
  touch-action: none;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 48px;
  width: 100%;
  padding: 8px 18px 8px 16px;
  border: 1px solid var(--semantic-tokens-neutral-weak-border);
  border-radius: 4px;
  @include textToken.headline-medium;

  // 点滅カーソルを表示する
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 16px;
    width: 1px;
    height: 28px;
    background-color: var(--semantic-tokens-neutral-default-text);
    animation: blink 1s infinite;
  }
  @keyframes blink {
    0% {
      opacity: 1;
    }
    25% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
    75% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
}

.check-item-title {
  word-break: break-all;
  overflow-wrap: break-word;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
}

.image-block {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 108px;
  height: 72px;

  &.blank-image {
    border: 1px dashed var(--semantic-tokens-neutral-weak-border);
  }
}
</style>
